import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import './PaymentSystem.css';

/**
 * CollaborationCelebration - 2025 Design (Merged Modal)
 *
 * Duolingo-style session end modal with token rewards
 * Shows: tokens earned, multipliers, quests, streaks, mystery bonus
 *
 * Snapchat Model Integration (December 2025):
 * Study buddy prompt is now INLINE - not a separate modal.
 * One tap to dismiss, with optional "Add as Study Buddy" included.
 * Gen Z friendly: FaceTime simplicity - everything in one screen.
 */
const CollaborationCelebration = ({
  isVisible,
  onClose,
  sessionDuration = 0,
  friendName = null,
  focusMultiplier = 1,
  sessionCount = 1,
  roomToken = null,
  coveredByHostSubscription = false,
  // Token economy props
  tokensEarned = 0,
  breakdown = null,
  mysteryBonus = 0,
  quests = null,
  streakDays = 0,
  newBalance = 0,
  isAuthenticated = false,
  showTokens = true,
  // Study buddy props (Snapchat model - December 2025)
  studyPartner = null, // { user_id, name, avatar_initial } - who you studied with
  sessionId = null,    // For FERPA audit trail
  onStudyBuddyResponse = null // callback when user responds to study buddy prompt
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMysteryReveal, setShowMysteryReveal] = useState(false);
  const [animatedTokens, setAnimatedTokens] = useState(0);
  // Study buddy inline toggle (Snapchat model - merged into single modal)
  const [wantsStudyBuddy, setWantsStudyBuddy] = useState(true); // Default to yes - Gen Z optimistic
  const [submittingBuddy, setSubmittingBuddy] = useState(false);

  // Check if study buddy option should be shown
  const showStudyBuddyOption = studyPartner && studyPartner.user_id && isAuthenticated;
  const partnerFirstName = studyPartner?.name?.split(' ')[0] || 'Friend';

  // Handle close with study buddy submission (merged flow)
  const handleClose = async () => {
    if (showStudyBuddyOption && wantsStudyBuddy) {
      setSubmittingBuddy(true);
      try {
        await axios.post('/api/study-buddies/prompt', {
          session_id: sessionId,
          other_user_id: studyPartner.user_id,
          response: 'yes'
        });
        if (onStudyBuddyResponse) {
          onStudyBuddyResponse('yes');
        }
      } catch (err) {
        console.error('Error submitting study buddy response:', err);
      }
      setSubmittingBuddy(false);
    } else if (showStudyBuddyOption && !wantsStudyBuddy) {
      // User opted out - record "maybe_later"
      try {
        await axios.post('/api/study-buddies/prompt', {
          session_id: sessionId,
          other_user_id: studyPartner.user_id,
          response: 'maybe_later'
        });
        if (onStudyBuddyResponse) {
          onStudyBuddyResponse('maybe_later');
        }
      } catch (err) {
        console.error('Error recording study buddy opt-out:', err);
      }
    }
    onClose();
  };

  // Reset state when modal becomes visible
  useEffect(() => {
    if (isVisible) {
      setWantsStudyBuddy(true); // Reset to default optimistic
    }
  }, [isVisible]);

  // Animate token count on mount
  useEffect(() => {
    if (isVisible && tokensEarned > 0) {
      const duration = 1000; // 1 second
      const steps = 20;
      const increment = tokensEarned / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= tokensEarned) {
          setAnimatedTokens(tokensEarned);
          clearInterval(timer);
          // Show mystery bonus after token animation
          if (mysteryBonus > 0) {
            setTimeout(() => setShowMysteryReveal(true), 500);
          }
        } else {
          setAnimatedTokens(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isVisible, tokensEarned, mysteryBonus]);

  if (!isVisible) return null;

  const minutes = Math.floor(sessionDuration / 60);
  const isFriendSession = friendName && friendName !== 'your friend';
  const showConversion = sessionCount >= 2 && !coveredByHostSubscription && !isAuthenticated;
  const hasTokenData = showTokens && isAuthenticated && (tokensEarned > 0 || breakdown);

  // Bloom level display config
  const bloomConfig = {
    'remembering': { icon: '📝', color: '#94A3B8', label: 'Remembering' },
    'understanding': { icon: '💡', color: '#60A5FA', label: 'Understanding' },
    'applying': { icon: '🎯', color: '#34D399', label: 'Applying' },
    'analyzing': { icon: '🔍', color: '#FBBF24', label: 'Analyzing' },
    'evaluating': { icon: '⚖️', color: '#F97316', label: 'Evaluating' },
    'creating': { icon: '✨', color: '#E879F9', label: 'Creating' }
  };

  const currentBloom = breakdown?.bloom_level ? bloomConfig[breakdown.bloom_level.toLowerCase()] : null;

  const handleGetUnlimited = async () => {
    setLoading(true);
    setError(null);

    try {
      await axios.get('/api/guest/csrf-token').catch(() => {});

      const response = await axios.post('/api/payment/create-checkout-session', {
        tier: 'unlimited',
        billing_frequency: 'monthly',
        conversion_source: 'study_buddy_friend',
        friend_context: {
          helped_friend: friendName,
          session_count: sessionCount,
          total_minutes: minutes,
          create_account_after_payment: true,
          room_token: roomToken
        }
      }, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      });

      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Payment error:', err);
      setError('Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  // Calculate completed quests
  const completedQuests = quests ? Object.values(quests).filter(q => q.complete).length : 0;
  const totalQuests = quests ? Object.keys(quests).length : 3;

  return (
    <AnimatePresence>
      {isVisible && (
          <motion.div
            className="celebration-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && !submittingBuddy && handleClose()}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <motion.div
            className="celebration-modal"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', duration: 0.4 }}
            style={{
              background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#fff',
              textAlign: 'center'
            }}
          >
            {/* Token Economy View (Authenticated Users) */}
            {hasTokenData && !showConversion ? (
              <>
                {/* Celebration Header */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  style={{ fontSize: '48px', marginBottom: '16px' }}
                >
                  🎉
                </motion.div>

                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  marginBottom: '8px',
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Great Session!
                </h2>

                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '24px'
                }}>
                  {minutes} minutes of focused learning
                </p>

                {/* Token Earned - Main Display */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 165, 0, 0.1))',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '20px'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '32px' }}>🪙</span>
                    <span style={{
                      fontSize: '36px',
                      fontWeight: '800',
                      color: '#FFD700',
                      fontVariantNumeric: 'tabular-nums'
                    }}>
                      +{animatedTokens}
                    </span>
                  </div>

                  {/* Multiplier Badges */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {breakdown?.flow_state && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.4 }}
                        style={{
                          background: 'linear-gradient(135deg, #F59E0B, #F97316)',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        🔥 Flow State 3x
                      </motion.span>
                    )}
                    {currentBloom && breakdown?.bloom_multiplier > 1 && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5 }}
                        style={{
                          background: `${currentBloom.color}33`,
                          border: `1px solid ${currentBloom.color}`,
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: currentBloom.color
                        }}
                      >
                        {currentBloom.icon} {currentBloom.label}
                      </motion.span>
                    )}
                  </div>

                  {/* Breakdown */}
                  {breakdown && (
                    <p style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      marginTop: '12px'
                    }}>
                      {breakdown.questions_count} questions × {breakdown.total_multiplier?.toFixed(1) || '1.0'}x
                    </p>
                  )}
                </motion.div>

                {/* Mystery Bonus Reveal */}
                <AnimatePresence>
                  {showMysteryReveal && mysteryBonus > 0 && (
                    <motion.div
                      initial={{ scale: 0, rotate: -10 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0 }}
                      transition={{ type: 'spring', stiffness: 500 }}
                      style={{
                        background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '20px'
                      }}
                    >
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>✨</div>
                      <div style={{ fontWeight: '700', fontSize: '16px' }}>MYSTERY BONUS!</div>
                      <div style={{ fontSize: '24px', fontWeight: '800' }}>+{mysteryBonus} tokens</div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Streak Display */}
                {streakDays > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '20px',
                      padding: '12px',
                      background: 'rgba(249, 115, 22, 0.15)',
                      borderRadius: '12px',
                      border: '1px solid rgba(249, 115, 22, 0.3)'
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>🔥</span>
                    <span style={{ fontWeight: '600', color: '#F97316' }}>
                      {streakDays} day streak!
                    </span>
                  </motion.div>
                )}

                {/* Daily Quests Progress */}
                {quests && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '24px',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <span style={{ fontSize: '14px', fontWeight: '600' }}>📋 Daily Quests</span>
                      <span style={{
                        fontSize: '12px',
                        color: completedQuests === totalQuests ? '#34D399' : 'rgba(255,255,255,0.6)'
                      }}>
                        {completedQuests}/{totalQuests}
                      </span>
                    </div>

                    {Object.entries(quests).map(([key, quest]) => (
                      <div
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '8px',
                          opacity: quest.complete ? 1 : 0.6
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>
                          {quest.complete ? '✅' : '⬜'}
                        </span>
                        <span style={{
                          flex: 1,
                          fontSize: '13px',
                          textDecoration: quest.complete ? 'line-through' : 'none',
                          color: quest.complete ? '#34D399' : 'rgba(255,255,255,0.8)'
                        }}>
                          {quest.label}
                        </span>
                        <span style={{
                          fontSize: '12px',
                          color: quest.complete ? '#34D399' : 'rgba(255,255,255,0.5)'
                        }}>
                          {quest.progress}/{quest.target}
                        </span>
                      </div>
                    ))}
                  </motion.div>
                )}

                {/* New Balance */}
                {newBalance > 0 && (
                  <p style={{
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.5)',
                    marginBottom: showStudyBuddyOption ? '16px' : '20px'
                  }}>
                    Balance: {newBalance} tokens
                  </p>
                )}

                {/* Study Buddy Toggle (Inline - Snapchat Model) */}
                {showStudyBuddyOption && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    style={{
                      background: wantsStudyBuddy
                        ? 'rgba(41, 122, 146, 0.15)'
                        : 'rgba(255, 255, 255, 0.05)',
                      border: wantsStudyBuddy
                        ? '1px solid rgba(41, 122, 146, 0.4)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '14px',
                      padding: '14px 16px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => setWantsStudyBuddy(!wantsStudyBuddy)}
                  >
                    {/* Partner Avatar */}
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #297A92, #6DB4CD)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 600,
                      flexShrink: 0
                    }}>
                      {studyPartner.avatar_initial || partnerFirstName.charAt(0).toUpperCase()}
                    </div>

                    {/* Text */}
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'white',
                        marginBottom: '2px'
                      }}>
                        See when {partnerFirstName} is online?
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }}>
                        {wantsStudyBuddy ? 'Will add if they add you too' : 'Tap to add as study buddy'}
                      </div>
                    </div>

                    {/* Toggle */}
                    <div style={{
                      width: '44px',
                      height: '26px',
                      borderRadius: '13px',
                      background: wantsStudyBuddy
                        ? 'linear-gradient(135deg, #297A92, #42909E)'
                        : 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px',
                      transition: 'all 0.2s ease',
                      flexShrink: 0
                    }}>
                      <motion.div
                        animate={{ x: wantsStudyBuddy ? 18 : 0 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        style={{
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          background: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      />
                    </div>
                  </motion.div>
                )}

                {/* Done Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleClose}
                  disabled={submittingBuddy}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: submittingBuddy
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'linear-gradient(135deg, #10B981, #0EA5E9)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: submittingBuddy ? 'default' : 'pointer',
                    opacity: submittingBuddy ? 0.7 : 1
                  }}
                >
                  {submittingBuddy ? 'Saving...' : 'Continue'}
                </motion.button>
              </>
            ) : !showConversion ? (
              // Non-authenticated or no token data: Simple session summary
              <>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>

                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  marginBottom: '8px',
                  color: '#fff'
                }}>
                  Great session!
                </h2>

                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '24px'
                }}>
                  {isFriendSession ? (
                    <>
                      {friendName} stayed focused for <strong>{minutes} minutes</strong>
                      <br />
                      <span style={{ color: '#10B981' }}>
                        {focusMultiplier}x their usual attention span
                      </span>
                    </>
                  ) : (
                    <>{minutes} minutes of learning</>
                  )}
                </p>

                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '24px',
                  marginBottom: '24px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#10B981' }}>
                      {minutes}
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                      minutes
                    </div>
                  </div>
                  {isFriendSession && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#0EA5E9' }}>
                        {focusMultiplier}x
                      </div>
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                        focus boost
                      </div>
                    </div>
                  )}
                </div>

                {/* Study Buddy Toggle for non-token users */}
                {showStudyBuddyOption && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    style={{
                      background: wantsStudyBuddy
                        ? 'rgba(41, 122, 146, 0.15)'
                        : 'rgba(255, 255, 255, 0.05)',
                      border: wantsStudyBuddy
                        ? '1px solid rgba(41, 122, 146, 0.4)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '14px',
                      padding: '14px 16px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => setWantsStudyBuddy(!wantsStudyBuddy)}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #297A92, #6DB4CD)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 600,
                      flexShrink: 0
                    }}>
                      {studyPartner.avatar_initial || partnerFirstName.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'white',
                        marginBottom: '2px'
                      }}>
                        See when {partnerFirstName} is online?
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }}>
                        {wantsStudyBuddy ? 'Will add if they add you too' : 'Tap to add as study buddy'}
                      </div>
                    </div>
                    <div style={{
                      width: '44px',
                      height: '26px',
                      borderRadius: '13px',
                      background: wantsStudyBuddy
                        ? 'linear-gradient(135deg, #297A92, #42909E)'
                        : 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px',
                      transition: 'all 0.2s ease',
                      flexShrink: 0
                    }}>
                      <motion.div
                        animate={{ x: wantsStudyBuddy ? 18 : 0 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        style={{
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          background: 'white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      />
                    </div>
                  </motion.div>
                )}

                <button
                  onClick={handleClose}
                  disabled={submittingBuddy}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: submittingBuddy
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'linear-gradient(135deg, #10B981, #0EA5E9)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: submittingBuddy ? 'default' : 'pointer',
                    opacity: submittingBuddy ? 0.7 : 1
                  }}
                >
                  {submittingBuddy ? 'Saving...' : 'Done'}
                </button>
              </>
            ) : (
              // Session 2+: Conversion with payment (for guests)
              <>
                {error && (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: '#EF4444'
                  }}>
                    {error}
                  </div>
                )}

                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤝</div>

                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  marginBottom: '8px',
                  color: '#fff'
                }}>
                  You've helped friends {sessionCount} times
                </h2>

                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '24px'
                }}>
                  Be their study buddy anytime
                </p>

                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '24px',
                  textAlign: 'left'
                }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                    With unlimited access:
                  </p>
                  <ul style={{
                    margin: 0,
                    paddingLeft: '20px',
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.8
                  }}>
                    <li>Help friends stay focused</li>
                    <li>They can help you back</li>
                    <li>Study together anytime</li>
                    <li>Cancel whenever</li>
                  </ul>
                </div>

                <button
                  onClick={handleGetUnlimited}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: loading ? '#666' : 'linear-gradient(135deg, #10B981, #0EA5E9)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginBottom: '8px'
                  }}
                >
                  {loading ? 'Opening checkout...' : 'Get Unlimited — $9.99/mo'}
                </button>

                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  marginBottom: '16px'
                }}>
                  Apple Pay, Google Pay & cards accepted
                </p>

                <button
                  onClick={handleClose}
                  disabled={loading || submittingBuddy}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.6)',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Maybe later
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CollaborationCelebration;
